import { select, coerceArray } from '@ngneat/elf';
import { pipe, distinctUntilKeyChanged, EMPTY, defer } from 'rxjs';
import { distinctUntilKeyChanged as distinctUntilKeyChanged$1, tap, map, distinctUntilChanged } from 'rxjs/operators';

function withRequestsCache(initialValue) {
  return {
    props: {
      requestsCache: initialValue != null ? initialValue : {}
    },
    config: undefined
  };
}
function selectRequestCache(key) {
  return pipe(distinctUntilKeyChanged('requestsCache'), select(state => getRequestCache(key)(state)));
}
function updateRequestsCache(requestsOrKeys, value) {
  let normalized = requestsOrKeys;

  if (value) {
    normalized = requestsOrKeys.reduce((acc, key) => {
      acc[key] = value;
      return acc;
    }, {});
  }

  return function (state) {
    return Object.assign({}, state, {
      requestsCache: Object.assign({}, state.requestsCache, normalized)
    });
  };
}
function updateRequestCache(key, {
  ttl,
  value: v
} = {}) {
  const data = {
    value: v != null ? v : 'full'
  };

  if (ttl) {
    data.timestamp = Date.now() + ttl;
  }

  return function (state) {
    return Object.assign({}, state, {
      requestsCache: Object.assign({}, state.requestsCache, {
        [key]: data
      })
    });
  };
}
function getRequestCache(key) {
  return function (state) {
    var _state$requestsCache$;

    const cacheValue = (_state$requestsCache$ = state.requestsCache[key]) != null ? _state$requestsCache$ : {
      value: 'none'
    };

    if (cacheValue.timestamp && cacheValue.timestamp < Date.now()) {
      return {
        value: 'none'
      };
    }

    return cacheValue;
  };
}
function selectIsRequestCached(key, options) {
  return pipe(distinctUntilKeyChanged('requestsCache'), select(state => isRequestCached(key, options)(state)));
}
function isRequestCached(key, options) {
  return function (state) {
    var _options$value;

    const type = (_options$value = options == null ? void 0 : options.value) != null ? _options$value : 'full';
    return coerceArray(key).some(k => getRequestCache(k)(state).value === type);
  };
}
function skipWhileCached(store, key, options) {
  return function (source) {
    if (store.query(isRequestCached(key, {
      value: options == null ? void 0 : options.value
    }))) {
      var _options$returnSource;

      return (_options$returnSource = options == null ? void 0 : options.returnSource) != null ? _options$returnSource : EMPTY;
    }

    return source;
  };
}
function createRequestsCacheOperator(store) {
  return function (key, options) {
    return skipWhileCached(store, key, options);
  };
}
function clearRequestsCache() {
  return function (state) {
    return Object.assign({}, state, {
      requestsCache: {}
    });
  };
}

function withRequestsStatus(initialValue) {
  return {
    props: {
      requestsStatus: initialValue != null ? initialValue : {}
    },
    config: undefined
  };
}
function updateRequestsStatus(requestsOrKeys, value, error) {
  let normalized = requestsOrKeys;

  if (value) {
    normalized = requestsOrKeys.reduce((acc, key) => {
      acc[key] = resolveStatus(value, error);
      return acc;
    }, {});
  }

  return function (state) {
    return Object.assign({}, state, {
      requestsStatus: Object.assign({}, state.requestsStatus, normalized)
    });
  };
}

function resolveStatus(value, error) {
  const newStatus = {
    value
  };

  if (value === 'error') {
    newStatus.error = error;
  }

  return newStatus;
}

function updateRequestStatus(key, value, error) {
  return function (state) {
    return Object.assign({}, state, {
      requestsStatus: Object.assign({}, state.requestsStatus, {
        [key]: resolveStatus(value, error)
      })
    });
  };
}
function getRequestStatus(key) {
  return function (state) {
    var _state$requestsStatus;

    return (_state$requestsStatus = state.requestsStatus[key]) != null ? _state$requestsStatus : {
      value: 'idle'
    };
  };
}
function selectRequestStatus(key, options) {
  return pipe(distinctUntilKeyChanged$1('requestsStatus'), select(state => {
    const base = getRequestStatus(key)(state);

    if (options != null && options.groupKey) {
      const parent = getRequestStatus(options.groupKey)(state);
      return parent.value === 'success' ? parent : base;
    }

    return base;
  }));
}
function selectIsRequestPending(key) {
  return pipe(distinctUntilKeyChanged$1('requestsStatus'), select(state => getRequestStatus(key)(state).value === 'pending'));
}
function trackRequestStatus(store, key, options) {
  return function (source) {
    return defer(() => {
      if (store.query(getRequestStatus(key)).value !== 'pending') {
        store.update(updateRequestStatus(key, 'pending'));
      }

      return source.pipe(tap({
        error(error) {
          store.update(updateRequestStatus(key, 'error', options != null && options.mapError ? options == null ? void 0 : options.mapError(error) : error));
        }

      }));
    });
  };
}
function createRequestsStatusOperator(store) {
  return function (key, options) {
    return trackRequestStatus(store, key, options);
  };
}
function initializeAsPending(keys) {
  return coerceArray(keys).reduce((acc, key) => {
    acc[key] = {
      value: 'pending'
    };
    return acc;
  }, {});
}
function clearRequestsStatus() {
  return function (state) {
    return Object.assign({}, state, {
      requestsStatus: {}
    });
  };
}

function createRequestDataSource(params) {
  const {
    data$,
    store,
    dataKey,
    requestStatusOptions,
    requestKey,
    idleAsPending = false
  } = Reflect.has(params, 'requestKey') ? params : Object.assign({}, params, {
    requestKey: undefined
  });
  return {
    trackRequestStatus: options => trackRequestStatus(store, requestKey != null ? requestKey : options == null ? void 0 : options.key, options),
    skipWhileCached: options => skipWhileCached(store, requestKey != null ? requestKey : options == null ? void 0 : options.key, options),

    setSuccess(options) {
      return updateRequestStatus(requestKey != null ? requestKey : options == null ? void 0 : options.key, 'success');
    },

    setCached(options) {
      return updateRequestCache(requestKey != null ? requestKey : options == null ? void 0 : options.key, options);
    },

    data$: options => store.combine({
      data: data$(requestKey != null ? requestKey : options == null ? void 0 : options.key),
      status: store.pipe(selectRequestStatus(requestKey != null ? requestKey : options == null ? void 0 : options.key, requestStatusOptions || {}))
    }).pipe(map(({
      data,
      status
    }) => {
      return {
        [dataKey]: data,
        loading: idleAsPending ? status.value === 'pending' || status.value === 'idle' : status.value === 'pending',
        error: status.value === 'error' ? status.error : undefined
      };
    }), distinctUntilChanged((a, b) => {
      // if the status is the same, for example, `pending` and `pending`, and the `data` is the same
      // don't emit a redundant value
      return a[dataKey] === b[dataKey] && a.loading === b.loading && a.error === b.error;
    }))
  };
}

export { clearRequestsCache, clearRequestsStatus, createRequestDataSource, createRequestsCacheOperator, createRequestsStatusOperator, getRequestCache, getRequestStatus, initializeAsPending, isRequestCached, selectIsRequestCached, selectIsRequestPending, selectRequestCache, selectRequestStatus, updateRequestCache, updateRequestStatus, updateRequestsCache, updateRequestsStatus, withRequestsCache, withRequestsStatus };
